import { Component, signal, output, input, inject, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { arabicCharactersValidator, englishCharactersValidator } from '../../../admin-dashboard-component/products/edit-product-form/utils/validation';

@Component({
  selector: 'app-add-new-value',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './add-new-value.html',
    styleUrl: './add-new-value.scss',
})
export class AddNewValue implements OnChanges {
  private fb = inject(FormBuilder);

  existingValues = input<{ en: string; ar: string | null }[]>([]);
  editData = input<{ en: string; ar: string | null; index?: number } | null>(null);
  
  onSave = output<{ en: string; ar: string }>();
  onClose = output<void>();

  valueForm = this.fb.group({
    inputEn: ['', [
      Validators.maxLength(100), 
      englishCharactersValidator()
    ]],
    inputAr: ['', [
      Validators.required,
      Validators.maxLength(100), 
      arabicCharactersValidator()
    ]]
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editData'] && this.editData()) {
      this.valueForm.patchValue({
        inputEn: this.editData()?.en ?? '',
        inputAr: this.editData()?.ar ?? ''
      });
    }
  }

  open() {
    const data = this.editData();
    this.valueForm.reset({
      inputEn: data?.en ?? '',
      inputAr: data?.ar ?? ''
    });
  }

  invalid(controlName: string): boolean {
    const control = this.valueForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  err(controlName: string, errorName: string): boolean {
    return !!this.valueForm.get(controlName)?.hasError(errorName);
  }

  save(event: Event) {
    event.preventDefault();

    if (this.valueForm.invalid) {
      this.valueForm.markAllAsTouched();
      return;
    }

    const rawValues = this.valueForm.getRawValue();
    const trimmedEn = rawValues.inputEn?.trim() || '';
    const trimmedAr = rawValues.inputAr?.trim() || '';

    const editIdx = this.editData()?.index ?? -1;
    
    const isEnDup = !!trimmedEn && this.existingValues()
      .filter((_, i) => i !== editIdx)
      .some(v => v.en.toLowerCase() === trimmedEn.toLowerCase());

    if (isEnDup) {
      this.valueForm.get('inputEn')?.setErrors({ alreadyExists: true });
      return;
    }

    if (trimmedAr) {
      const isArDup = this.existingValues()
        .filter((_, i) => i !== editIdx)
        .some(v => v.ar?.toLowerCase() === trimmedAr.toLowerCase());
      
      if (isArDup) {
        this.valueForm.get('inputAr')?.setErrors({ alreadyExists: true });
        return;
      }
    }

    this.onSave.emit({ en: trimmedEn, ar: trimmedAr });
    this.valueForm.reset();
  }

  close() {
    this.valueForm.reset();
    this.onClose.emit();
  }
}